import { Button } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import { alpha, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import type { Brief } from './briefRows.js';
import { briefsOf } from './briefRows.js';
import { type CreativeDetail, CreativeDetailOpener } from './CreativeDetailOpener.js';
import { CreativeRows } from './CreativeRows.js';
import { RecommendationCard } from './RecommendationCard.js';
import { columnsOf, type ResultRow, rowsOf } from './resultRows.js';
import type { WorkflowRun } from './workflows.types.js';

const LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  color: 'text.secondary',
};

// Each block is ruled off from the one above rather than boxed, and the rule
// carries the spacing — a gap on top of it doubles the distance.
const SECTION_SX = {
  gap: 2,
  pt: 4,
  pb: 2,
  borderTop: '1px solid',
  borderColor: 'divider',
};

// A real textarea: the writer's paragraph runs long, so it wraps and scrolls
// rather than being clipped to the width of the box.
const ADDITIONAL_INFO_SX = {
  width: '100%',
  boxSizing: 'border-box',
  px: 2,
  py: 1.5,
  borderRadius: 1.2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.secondary',
  font: 'inherit',
  fontSize: 12,
  lineHeight: 1.5,
  resize: 'none',
};

// The design draws these as dropdowns; a brief is not editable, so they read as
// the values they were cut under.
const READONLY_FIELD_SX = {
  px: 2,
  py: 1.5,
  borderRadius: 1.2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  fontSize: 12,
  fontWeight: 600,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '@media print': { whiteSpace: 'normal' },
};

const UNSET = '—';

const STRIP_SX = (theme: Theme) => ({
  mx: 3,
  mb: 3,
  px: 2.4,
  py: 1.6,
  gap: 2,
  borderRadius: 0.9,
  border: '1px solid',
  borderColor: alpha(theme.palette.success.dark, 0.3),
  bgcolor: alpha(theme.palette.success.dark, 0.1),
  alignItems: 'center',
});

const STRIP_LABEL_SX = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  color: 'text.secondary',
};

// Alison 1's RunNavigator, in this theme: the radii are in px-equivalents
// because the two apps scale `borderRadius` from different bases (4 vs 10).
const NAV_SX = {
  // Sized to its own content and centred: full width pushed the arrows to the
  // far edges of the drawer, a reach away from the label they move.
  alignSelf: 'center',
  minWidth: 280,
  maxWidth: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  p: 1.5,
  borderRadius: 1.2,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 6,
};

const NAV_BUTTON_SX = { border: '1px solid', borderColor: 'divider', borderRadius: 0.8 };

const ONE_LINE = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

interface Briefed {
  row: ResultRow;
  brief: Brief;
}

function summary(brief: Brief): string {
  const count = brief.recommendations.length;
  const kpi = brief.settings.find((entry) => entry.label === 'KPI to improve')?.value ?? null;
  const aim = kpi === null ? '' : `, aimed at improving ${kpi}`;
  return (
    `A prioritized set of ${count} element-level change${count === 1 ? '' : 's'} to ship a ` +
    `stronger next version${aim}. Review each recommendation and its rationale below, then ` +
    `send the set to creative generation — or hand it to your designers — to build the next cut.`
  );
}

export function BriefField({ label, value }: { label: string; value: string | null }): ReactNode {
  return (
    <Stack sx={{ gap: 1, minWidth: 0 }}>
      <Typography sx={LABEL_SX}>{label.toUpperCase()}</Typography>
      <Box sx={READONLY_FIELD_SX}>{value === null || value === '' ? UNSET : value}</Box>
    </Stack>
  );
}

// `printable` lays the brief out for paper: a textarea prints only the lines it shows.
export function BriefCard({
  brief,
  printable = false,
}: {
  brief: Brief;
  printable?: boolean;
}): ReactNode {
  const { providerName } = useTenant();
  return (
    <Stack data-testid={`brief-${brief.creativeId}`}>
      <Stack sx={SECTION_SX}>
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'flex-start' }}>
          <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', mt: 0.25, flexShrink: 0 }} />
          <Stack sx={{ gap: 0.5, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main' }}>
              What to do with this brief?
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{summary(brief)}</Typography>
          </Stack>
        </Stack>
      </Stack>

      <Stack sx={SECTION_SX}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          <Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Preferences:{' '}
          </Box>
          {brief.preferences.map((entry) => `${entry.label}: ${entry.value ?? UNSET}`).join(' · ')}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 2 }}>
          {brief.settings.map((setting) => (
            <BriefField key={setting.label} label={setting.label} value={setting.value} />
          ))}
        </Box>

        {brief.body === '' ? null : (
          <Stack sx={{ gap: 1 }}>
            <Typography sx={LABEL_SX}>ADDITIONAL INFO</Typography>
            {printable ? (
              <Box sx={{ ...ADDITIONAL_INFO_SX, whiteSpace: 'pre-wrap' }}>{brief.body}</Box>
            ) : (
              <Box
                component="textarea"
                readOnly
                rows={4}
                value={brief.body}
                aria-label="Additional info"
                sx={ADDITIONAL_INFO_SX}
              />
            )}
          </Stack>
        )}
      </Stack>

      <Stack sx={SECTION_SX}>
        <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
          <AutoAwesomeOutlined sx={{ fontSize: 14, color: 'primary.main' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {providerName} iteration recommendations ({brief.recommendations.length})
          </Typography>
        </Stack>
        {brief.recommendations.map((recommendation, index) => (
          <RecommendationCard
            key={recommendation.key}
            recommendation={recommendation}
            flush
            ruled={index > 0}
          />
        ))}
      </Stack>
    </Stack>
  );
}

// Both marks take their colour from `currentColor`; the check stays a knockout
// on the badge. The source SVGs clip to their own viewBox, which nothing
// overflows, so the clip paths are dropped rather than given unique ids per row.
function BriefCreatedMark(): ReactNode {
  return (
    <SvgIcon
      viewBox="0 0 16 16"
      sx={{ fontSize: 16, color: 'success.dark', flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M10.8538 3.36577L7.35375 0.135C7.30728 0.0921535 7.25212 0.0581778 7.19143 0.0350141C7.13073 0.0118504 7.06568 -4.75319e-05 7 1.42707e-07H1C0.734784 1.42707e-07 0.48043 0.0972526 0.292893 0.270363C0.105357 0.443474 0 0.678262 0 0.923077V11.0769C0 11.3217 0.105357 11.5565 0.292893 11.7296C0.48043 11.9027 0.734784 12 1 12H10C10.2652 12 10.5196 11.9027 10.7071 11.7296C10.8946 11.5565 11 11.3217 11 11.0769V3.69231C11.0001 3.63168 10.9872 3.57164 10.9621 3.51561C10.937 3.45958 10.9002 3.40866 10.8538 3.36577ZM7.5 1.57558L9.29313 3.23077H7.5V1.57558ZM10 11.0769H1V0.923077H6.5V3.69231C6.5 3.81472 6.55268 3.93211 6.64645 4.01866C6.74021 4.10522 6.86739 4.15385 7 4.15385H10V11.0769Z"
      />
      <rect
        x="6.5"
        y="6.5"
        width="9"
        height="9"
        rx="4.5"
        fill="currentColor"
        stroke="currentColor"
      />
      <path
        fill="white"
        stroke="white"
        strokeWidth="0.2"
        d="M13.5 9.12891C13.6094 9.12897 13.7146 9.17171 13.792 9.24902C13.8695 9.32648 13.9131 9.43245 13.9131 9.54199C13.913 9.65138 13.8693 9.75663 13.792 9.83398L13.7207 9.9043L13.7197 9.90332L10.458 13.166C10.4197 13.2044 10.3743 13.235 10.3242 13.2559C10.2741 13.2767 10.2203 13.2881 10.166 13.2881C10.1117 13.2881 10.058 13.2767 10.0078 13.2559C9.95768 13.235 9.91234 13.2045 9.87402 13.166L8.41504 11.708C8.37686 11.6698 8.34688 11.6241 8.32617 11.5742C8.30541 11.5241 8.29492 11.4703 8.29492 11.416C8.29492 11.3618 8.30542 11.3079 8.32617 11.2578C8.34688 11.2079 8.37686 11.1623 8.41504 11.124C8.45339 11.0857 8.49969 11.0549 8.5498 11.0342C8.59982 11.0135 8.65389 11.0029 8.70801 11.0029C8.76195 11.003 8.81538 11.0136 8.86523 11.0342C8.91535 11.0549 8.96165 11.0857 9 11.124L10.166 12.29L13.208 9.24902C13.2854 9.17181 13.3906 9.12891 13.5 9.12891Z"
      />
    </SvgIcon>
  );
}

function OpenDrawerMark(): ReactNode {
  return (
    <SvgIcon viewBox="0 0 16 16" sx={{ fontSize: 16, color: 'text.secondary' }} aria-hidden="true">
      <path
        fill="currentColor"
        d="M9.00014 8.49985V12.4998C9.00014 12.6324 8.94746 12.7595 8.8537 12.8533C8.75993 12.9471 8.63276 12.9997 8.50015 12.9997C8.36755 12.9997 8.24037 12.9471 8.14661 12.8533C8.05284 12.7595 8.00016 12.6324 8.00016 12.4998V9.70732L3.85401 13.8535C3.80755 13.8999 3.75241 13.9368 3.69171 13.9619C3.63102 13.9871 3.56596 14 3.50027 14C3.43457 14 3.36952 13.9871 3.30882 13.9619C3.24813 13.9368 3.19298 13.8999 3.14652 13.8535C3.10007 13.807 3.06322 13.7519 3.03808 13.6912C3.01294 13.6305 3 13.5654 3 13.4997C3 13.434 3.01294 13.369 3.03808 13.3083C3.06322 13.2476 3.10007 13.1924 3.14652 13.146L7.29268 8.99984H4.50024C4.36764 8.99984 4.24046 8.94716 4.1467 8.85339C4.05293 8.75963 4.00025 8.63245 4.00025 8.49985C4.00025 8.36724 4.05293 8.24007 4.1467 8.1463C4.24046 8.05254 4.36764 7.99986 4.50024 7.99986H8.50015C8.63276 7.99986 8.75993 8.05254 8.8537 8.1463C8.94746 8.24007 9.00014 8.36724 9.00014 8.49985ZM12 2H7.00021C6.735 2 6.48065 2.10535 6.29312 2.29289C6.10559 2.48042 6.00023 2.73477 6.00023 2.99998V5.99991C6.00023 6.13251 6.05291 6.25969 6.14667 6.35345C6.24044 6.44722 6.36761 6.4999 6.50022 6.4999C6.63282 6.4999 6.76 6.44722 6.85377 6.35345C6.94753 6.25969 7.00021 6.13251 7.00021 5.99991V2.99998H12V10.9998H11.0001C10.8675 10.9998 10.7403 11.0525 10.6465 11.1462C10.5528 11.24 10.5001 11.3672 10.5001 11.4998C10.5001 11.6324 10.5528 11.7596 10.6465 11.8533C10.7403 11.9471 10.8675 11.9998 11.0001 11.9998H12C12.2652 11.9998 12.5196 11.8944 12.7071 11.7069C12.8946 11.5193 13 11.265 13 10.9998V2.99998C13 2.73477 12.8946 2.48042 12.7071 2.29289C12.5196 2.10535 12.2652 2 12 2Z"
      />
    </SvgIcon>
  );
}

export function BriefCreatedStrip({
  count,
  onView,
}: {
  count: number;
  onView?: () => void;
}): ReactNode {
  return (
    <Stack direction="row" data-testid="brief-created" sx={STRIP_SX}>
      <BriefCreatedMark />
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={STRIP_LABEL_SX}>Brief created</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
          {count} recommendation{count === 1 ? '' : 's'} · Saved to Outcomes
        </Typography>
      </Stack>
      {onView === undefined ? null : (
        <Button
          variant="outlined"
          onClick={onView}
          endIcon={<OpenDrawerMark />}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', bgcolor: 'background.paper' }}
        >
          View brief
        </Button>
      )}
    </Stack>
  );
}

// The brief run echoes the creatives it was written from, so the same creative
// rows carry the brief under each one.
export function BriefRows({
  run,
  renderBrief,
  onOpen,
}: {
  run: WorkflowRun;
  renderBrief: (brief: Brief) => ReactNode;
  onOpen?: (row: ResultRow) => void;
}): ReactNode {
  const briefs = briefsOf(run.result);
  if (briefs.length === 0) return null;
  const rows = rowsOf(run.result, 'creative_list').filter((row) =>
    briefs.some((brief) => brief.creativeId === row.creativeId),
  );

  return (
    <CreativeRows
      rows={rows}
      columns={columnsOf(rows)}
      accented
      {...(onOpen === undefined ? {} : { onOpen })}
      renderDetail={(row) => {
        const brief = briefs.find((entry) => entry.creativeId === row.creativeId);
        return brief === undefined ? null : renderBrief(brief);
      }}
    />
  );
}

export function briefed(run: WorkflowRun): Briefed[] {
  const briefs = briefsOf(run.result);
  return rowsOf(run.result, 'creative_list').flatMap((row) => {
    const brief = briefs.find((entry) => entry.creativeId === row.creativeId);
    return brief === undefined ? [] : [{ row, brief }];
  });
}

function BriefNavigator({
  pages,
  at,
  onSelect,
}: {
  pages: Briefed[];
  at: number;
  onSelect: (index: number) => void;
}): ReactNode {
  const last = pages.length - 1;

  return (
    <Box data-testid="brief-navigator" sx={NAV_SX}>
      <IconButton
        size="small"
        aria-label="Previous brief"
        disabled={at === 0}
        onClick={() => onSelect(at - 1)}
        sx={NAV_BUTTON_SX}
      >
        <ChevronLeft fontSize="small" />
      </IconButton>

      <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, ...ONE_LINE }}>
          Brief {at + 1} of {pages.length}
        </Typography>
        <Typography
          sx={{ fontSize: 11, color: 'text.secondary', mt: 0.5, minHeight: 16, ...ONE_LINE }}
        >
          {pages[at]?.row.label ?? ''}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.6, mt: 1 }}>
          {pages.map((page, index) => (
            <Box
              key={page.brief.creativeId}
              component="button"
              type="button"
              aria-label={`Go to brief ${index + 1}`}
              onClick={() => onSelect(index)}
              sx={{
                p: 0,
                border: 0,
                cursor: 'pointer',
                height: 6,
                width: index === at ? 16 : 6,
                borderRadius: 999,
                bgcolor: index === at ? 'primary.main' : 'divider',
                transition: 'width .2s ease, background-color .2s ease',
              }}
            />
          ))}
        </Box>
      </Box>

      <IconButton
        size="small"
        aria-label="Next brief"
        disabled={at === last}
        onClick={() => onSelect(at + 1)}
        sx={NAV_BUTTON_SX}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    </Box>
  );
}

// The drawer reads one brief at a time: a run can cover a dozen creatives, and
// stacking every hand-off and its recommendations makes the one you opened for
// impossible to find.
export function BriefPager({
  pages,
  at,
  onSelect,
  creativeDetail,
}: {
  pages: Briefed[];
  at: number;
  onSelect: (index: number) => void;
  creativeDetail?: CreativeDetail & { workflowId: string };
}): ReactNode {
  const page = pages[at];
  if (page === undefined) return null;
  const renderRow = (onOpen?: (row: ResultRow) => void): ReactNode => (
    <CreativeRows
      plain
      rows={[page.row]}
      columns={columnsOf(pages.map((entry) => entry.row))}
      renderDetail={() => <BriefCard brief={page.brief} />}
      {...(onOpen === undefined ? {} : { onOpen })}
    />
  );

  return (
    <Stack sx={{ gap: 3 }}>
      {pages.length === 1 ? null : <BriefNavigator pages={pages} at={at} onSelect={onSelect} />}
      {creativeDetail === undefined ? (
        renderRow()
      ) : (
        <CreativeDetailOpener
          workflowId={creativeDetail.workflowId}
          rows={pages.map((entry) => entry.row)}
          creativeDetail={creativeDetail}
        >
          {renderRow}
        </CreativeDetailOpener>
      )}
    </Stack>
  );
}

// Every brief of the run, one to a page, for the drawer's download.
export function BriefPrintout({ pages, meta }: { pages: Briefed[]; meta: string }): ReactNode {
  const columns = columnsOf(pages.map((entry) => entry.row));
  return (
    <Stack data-testid="brief-printout" sx={{ gap: 6 }}>
      {pages.map((page, index) => (
        <Stack
          key={page.brief.creativeId}
          sx={{ gap: 3, ...(index === 0 ? {} : { breakBefore: 'page' }) }}
        >
          <Stack>
            <Typography sx={{ fontSize: 16, fontWeight: 700 }}>{page.brief.name}</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {[pages.length === 1 ? null : `Brief ${index + 1} of ${pages.length}`, meta]
                .filter((part) => part !== null && part !== '')
                .join(' · ')}
            </Typography>
          </Stack>
          <CreativeRows
            plain
            rows={[page.row]}
            columns={columns}
            renderDetail={() => <BriefCard brief={page.brief} printable />}
          />
        </Stack>
      ))}
    </Stack>
  );
}
